import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardComment from 'assets/components/card/themes/Theme.Card.Comment';
import ThemeCardListItem from 'assets/components/card/themes/Theme.Card.ListItem';
import Collection from 'assets/components/data/Collection';
import EditableDocumentList from 'assets/components/documentList/EditableDocumentList';
import useForm from 'assets/components/form/hooks/Form';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Sanitizer from 'assets/components/textBox/Sanitizer';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { date, defaultDateTimeFormat, defaultServerDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { isEmpty, orderBy } from 'lodash';
import User from 'models/core/user/Model.User';
import { useMemo } from 'react';
import { ContractRefundForm } from '../../../Sales.ContractRefund.Hooks';
import { refundCardStyles } from './Sales.ContractRefund.Process.Payees';

const SalesContractRefundProcessEndorsements = () => {
  const { lang } = getLocales();
  const { userId, user } = useAuthStore();
  const { form } = useModalForm<ContractRefundForm>();

  const commentForm = useForm<
    Utils.FormData<Model.IContractRefundComment & { tempCreatedAtUtcUnix: number; createdAtUtc: string; user: User }>
  >({
    default: {
      comment: undefined,
      user: user,
      userId: userId,
      contractRefundId: form.data?.id,
      createdAtUtc: date().format(defaultServerDateTimeFormat),
      tempCreatedAtUtcUnix: date().unix(),
    },
    onSubmit: async (newComment) => {
      const updatedComments = [...(form.data?.contractRefundComments ?? []), newComment];
      form.merge({ contractRefundComments: updatedComments });

      commentForm.reload();
    },
    validation: (data, errors) => {
      if (!data.comment) errors.comment = lang.required;
    },
  });

  const docForm = useForm<{ documents: Array<Utils.FormData<Model.IContractRefundDocument>> }>({
    default: { documents: [] },
    onSubmit: async ({ documents }) => {
      for (const document of documents) {
        const updateDocuments = [...(form.data?.contractRefundDocuments ?? []), { ...document }];
        form.merge({ contractRefundDocuments: updateDocuments });
      }
      docForm.reload();
    },
    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) errors.documents = { name: lang.required };
    },
  });
  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  return (
    <FormWrapper>
      <Card
        class={ThemeCardListItem}
        style={refundCardStyles}
        subtitle={() => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.L }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: Theme.Size.M,
                }}
              >
                {isEmpty(form.data.contractRefundComments) && <ErrorWrapper children={lang.noNotes} />}
                <Collection
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: Theme.Size.S,
                  }}
                  data={orderBy(form.data.contractRefundComments || [], (it) => it.tempCreatedAtUtcUnix, ['asc'])}
                  map={({ data: comment, index }) => (
                    <Card
                      info={localizeDate(comment?.createdAtUtc, defaultDateTimeFormat)}
                      title={comment.user?.displayInfo?.title}
                      media={<Icon class="fas-user" />}
                      subtitle={<Sanitizer text={comment?.comment} />}
                      class={ThemeCardComment}
                      actions={
                        <>
                          <Button
                            class={ThemeButtonCircle}
                            htmlElementProps={{ title: lang.delete }}
                            onClick={() => {
                              form.update({
                                contractRefundComments: form.data?.contractRefundComments?.filter(
                                  (_, i) => i !== index
                                ),
                              });
                            }}
                            media={<Icon class="fas-trash" />}
                          />
                        </>
                      }
                    />
                  )}
                />
                <div>
                  <MultilineTextInput name="comment" value={commentForm.data.comment} onChange={commentForm.update} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: Theme.Size.M }}>
                    <Button
                      class={ThemeButtonCircle}
                      onClick={commentForm.submit}
                      render={lang.addComent}
                      disabled={commentForm.hasErrors || commentForm.isSubmitted}
                      active={!commentForm.hasErrors}
                      isLoading={commentForm.isSubmitted}
                      media="fas-paper-plane"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
      <Card
        class={ThemeCardListItem}
        style={refundCardStyles}
        subtitle={() => {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.L }}>
              <div>
                <h3>{lang.documentManagement}</h3>
                &nbsp;
                <InputWrapper>
                  <FileInputSimple
                    name="files"
                    value={uploadedFiles}
                    onChange={({ files }) => {
                      docForm.update({
                        documents: [
                          ...(docForm.data.documents ?? []),
                          ...files.map((file) => ({
                            formFile: file.original,
                            name: file.original?.name,
                          })),
                        ],
                      });
                    }}
                    label={lang.documentUpload}
                  />
                  <Button
                    render={lang.add}
                    onClick={docForm.submit}
                    active={!docForm.hasErrors}
                    class={ThemeButtonCircle}
                    style={{ flexShrink: 0 }}
                    disabled={
                      docForm.hasErrors || docForm.isSubmitted || !docForm.isUpdated || !docForm.data?.documents?.length
                    }
                  />
                </InputWrapper>
                <br />
                <br />
                <EditableDocumentList
                  data={(docForm.data.documents ?? []) as Utils.Entities.IDocument[]}
                  onUpdate={(index, value) => {
                    const updated = [...docForm.data.documents];
                    updated[index] = { ...updated[index], ...value };
                    docForm.update({ documents: updated });
                  }}
                  onDelete={(index) => {
                    docForm.update({
                      documents: docForm.data.documents.filter((_, i) => i !== index),
                    });
                  }}
                />
              </div>
              <hr />
              <div>
                <h3>{lang.documents}</h3>
                &nbsp;
                <EditableDocumentList
                  data={(form.data.contractRefundDocuments ?? []) as Utils.Entities.IDocument[]}
                  onUpdate={(index, value) => {
                    const updated = [...form.data.contractRefundDocuments];
                    updated[index] = { ...updated[index], ...value };
                    form.update({ contractRefundDocuments: updated });
                  }}
                  onDelete={(index) => {
                    form.update({
                      contractRefundDocuments: form.data.contractRefundDocuments.filter((_, i) => i !== index),
                    });
                  }}
                />
              </div>
            </div>
          );
        }}
      />
    </FormWrapper>
  );
};

export default SalesContractRefundProcessEndorsements;
